(() => {
  if (customElements.get('evolved-accessory-picker')) return;
  class AccessoryPicker extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      this.buttonLabels = new WeakMap();
      this.mainVariantId = this.dataset.mainVariant;
      this.nonmagneticVariants = new Set(JSON.parse(this.dataset.nonmagneticVariantIds || '[]').map(String));
      this.stickyRoot = document.getElementById(`${this.dataset.form}-sticky-bar`);
      this.purchaseObserver?.disconnect();
      if (this.stickyRoot) {
        this.purchaseObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.purchaseFrame);
          this.purchaseFrame = requestAnimationFrame(() => this.update());
        });
        this.purchaseObserver.observe(this.stickyRoot, {childList:true, subtree:true});
      }
      this.root = this.closest('.ev-pdp-info') || this.closest('.product-info') || this.form;
      this.trigger = this.form.querySelector('[data-ev-accessory-toggle]');
      if (!this.trigger) return;
      this.trigger.addEventListener('click', () => this.setOpen(this.hidden), {signal});
      this.querySelector('[data-accessory-close]').addEventListener('click', () => {
        this.setOpen(false); this.trigger.focus();
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault(); this.setOpen(false); this.trigger.focus();
      }, {signal});
      this.addEventListener('change', event => {
        const row = event.target.closest('[data-accessory-row]');
        if (row) this.updateVariant(row);
        this.update();
      }, {signal});
      this.addEventListener('click', event => {
        const row = event.target.closest('[data-accessory-row]');
        if (!row || row.hidden || event.defaultPrevented || event.target.closest('select,option,input,button,a,label')) return;
        if (window.getSelection()?.type === 'Range') return;
        row.querySelector('[data-accessory-check]')?.click();
      }, {signal});
      this.form.addEventListener('variant:change', event => {
        this.mainVariantId = event.detail.variant?.id;
        this.refreshOffers(); this.update();
      }, {signal});
      this.form.addEventListener('product:rerender', () => queueMicrotask(() => {
        if (this.isConnected) this.update();
      }), {signal});
      this.form.addEventListener('variant:add', () => this.setOpen(false), {signal});
      this.form.addEventListener('cart:error', event => {
        this.querySelector('[role="status"]').textContent = event.detail.error;
      }, {signal});
      this.offerObserver?.disconnect();
      this.offerObserver = new MutationObserver(records => {
        const changed = records.some(record =>
          record.target.closest?.('.axe-checkbox-upsell-list') ||
          [...record.addedNodes].some(node => node.nodeType === 1 &&
            (node.matches('.axe-checkbox-upsell-list') || node.querySelector('.axe-checkbox-upsell-list'))));
        if (!changed) return;
        cancelAnimationFrame(this.offerFrame);
        this.offerFrame = requestAnimationFrame(() => { this.refreshOffers(); this.update(); });
      });
      this.offerObserver.observe(this.root, {childList:true, subtree:true, attributes:true, attributeFilter:['data-upsell-id']});
      this.refreshOffers(); this.update();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.offerObserver?.disconnect(); cancelAnimationFrame(this.offerFrame); cancelAnimationFrame(this.scrollFrame);
      this.purchaseObserver?.disconnect(); cancelAnimationFrame(this.purchaseFrame);
    }
    refreshOffers() {
      const offered = new Set([...this.root.querySelectorAll('.axe-checkbox-upsell-list .axe-input[data-upsell-id]')].map(input => input.dataset.upsellId));
      const nonmagnetic = this.nonmagneticVariants.has(String(this.mainVariantId));
      let visible = 0;
      this.querySelectorAll('[data-accessory-row]').forEach(row => {
        const eligible = !nonmagnetic && !offered.has(row.dataset.productId) && visible < 3;
        if (eligible) visible++;
        row.hidden = !eligible;
        if (!eligible) row.querySelector('[data-accessory-check]').checked = false;
      });
      this.trigger.hidden = !visible;
      if (!visible) this.hidden = true;
    }
    setOpen(open) {
      cancelAnimationFrame(this.scrollFrame);
      this.refreshOffers();
      this.hidden = !open || this.trigger.hidden;
      if (this.hidden) this.querySelectorAll('[data-accessory-check]').forEach(input => { input.checked = false; });
      this.querySelector('[role="status"]').textContent = '';
      this.update();
      if (!this.hidden) {
        this.querySelector('[data-accessory-row]:not([hidden]) [data-accessory-check]')?.focus({preventScroll:true});
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = requestAnimationFrame(() => this.keepPurchaseVisible());
        });
      }
    }
    keepPurchaseVisible() {
      if (this.hidden || !this.isConnected || !this.button?.isConnected) return;
      const rect = this.button.getBoundingClientRect();
      if (!rect.height) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight) - 24;
      if (rect.bottom <= bottom && rect.top >= top + 24) return;
      window.scrollBy({top:rect.bottom - bottom, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
    selected() {
      if (this.hidden) return [];
      return [...this.querySelectorAll('[data-accessory-row]')].filter(row => !row.hidden && row.querySelector('[data-accessory-check]').checked);
    }
    updateVariant(row) {
      const option = row.querySelector('select').selectedOptions[0];
      if (!option) return;
      const price = Number(option.dataset.cents), compare = Number(option.dataset.compareCents), sale = compare > price;
      row.querySelector('[data-accessory-price]').textContent = option.dataset.price;
      const compareNode = row.querySelector('[data-accessory-compare]'), savingNode = row.querySelector('[data-accessory-saving]');
      compareNode.textContent = option.dataset.compare; compareNode.hidden = !sale;
      savingNode.textContent = sale ? `Save ${Math.round((compare-price)*100/compare)}%` : ''; savingNode.hidden = !sale;
      const img = row.querySelector('img');
      if (img && option.dataset.image) { img.src = option.dataset.image; img.removeAttribute('srcset'); }
    }
    update() {
      if (!this.form?.isConnected) return;
      const rows = this.selected(), count = rows.length;
      const button = this.form.querySelector('buy-buttons button[type="submit"]');
      if (button && this.button !== button) {
        this.button = button;
        this.originalLabel = this.buttonLabel(button)?.textContent.trim() || '';
      }
      const purchaseButtons = [button, ...this.stickyRoot?.querySelectorAll('button[type="submit"]') || []].filter(Boolean);
      for (const purchaseButton of purchaseButtons) {
        if (purchaseButton.form !== this.form) continue;
        const label = this.buttonLabel(purchaseButton);
        if (!label) continue;
        if (!this.buttonLabels.has(purchaseButton) || !label.textContent.trim().startsWith('Add all to cart ·')) this.buttonLabels.set(purchaseButton, label.textContent.trim());
        const active = count > 0 && !button?.disabled && !purchaseButton.disabled;
        const copy = active ? `Add all to cart · ${count} ${count === 1 ? 'extra' : 'extras'}` : this.buttonLabels.get(purchaseButton);
        if (label.textContent.trim() !== copy) label.textContent = copy;
        purchaseButton.toggleAttribute('data-ev-add-selection', active);
      }
      this.trigger.setAttribute('aria-expanded', String(!this.hidden));
      this.trigger.querySelector('[data-accessory-trigger-state]').textContent = this.hidden ? '+' : '−';
      this.querySelectorAll('[data-accessory-row]').forEach(row => row.classList.toggle('is-selected', row.querySelector('[data-accessory-check]').checked));
      let subtotal = 0, saving = 0;
      rows.forEach(row => {
        const option = row.querySelector('select').selectedOptions[0];
        const price = Number(option.dataset.cents) || 0;
        subtotal += price; saving += Math.max(0, (Number(option.dataset.compareCents) || price)-price);
      });
      const money = value => new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency', currency:this.dataset.currency || 'USD'}).format(value/100);
      this.querySelector('[data-accessory-totals]').hidden = !count;
      this.querySelector('[data-accessory-count]').textContent = `Extras (${count})`;
      this.querySelector('[data-accessory-subtotal]').textContent = money(subtotal);
      const savings = this.querySelector('[data-accessory-total-saving]');
      savings.textContent = saving ? `You save ${money(saving)} on extras` : ''; savings.hidden = !saving;
    }
    buttonLabel(button) {
      return button.querySelector(':scope > div:not(.button__loader)') ||
        [...button.childNodes].find(node => node.nodeType === 3 && node.textContent.trim());
    }
  }
  customElements.define('evolved-accessory-picker', AccessoryPicker);
  window.EvolvedAccessorySelection = {
    prepare(data, submitter, form) {
      const picker = form.querySelector('evolved-accessory-picker');
      if (!picker || picker.hidden) return;
      const chosen = picker.selected().length;
      picker.refreshOffers();
      const rows = picker.selected();
      if (rows.length !== chosen) {
        picker.update();
        throw new Error('An accessory offer changed. Review your selection and try again.');
      }
      if (!rows.length) return;
      if (picker.button?.disabled) throw new Error('Choose an available product option first.');
      const entries = [...data.entries()];
      const indexes = entries.map(([key]) => key.match(/^items\[(\d+)\]/)?.[1]).filter(value => value !== undefined).map(Number);
      let next = indexes.length ? Math.max(...indexes) + 1 : 0;
      const mainId = data.get('id');
      if (mainId) {
        let mainIndex = indexes.find(index => String(data.get(`items[${index}][id]`)) === String(mainId));
        if (mainIndex === undefined) mainIndex = next++;
        entries.forEach(([key,value]) => {
          if (/^(id|quantity|selling_plan)$/.test(key)) { data.set(`items[${mainIndex}][${key}]`, value); data.delete(key); }
          else if (/^properties\[.+\]$/.test(key)) { data.append(`items[${mainIndex}][${key.slice(0,10)}]${key.slice(10)}`, value); data.delete(key); }
        });
        if (!data.has(`items[${mainIndex}][quantity]`)) data.set(`items[${mainIndex}][quantity]`, '1');
      } else if (!indexes.length) {
        throw new Error('Choose your device and color first.');
      }
      const existing = new Set([...data.entries()].filter(([key]) => /^items\[\d+\]\[id\]$/.test(key)).map(([,value]) => String(value)));
      for (const row of rows) {
        const id = row.querySelector('select').value;
        if (existing.has(id)) continue;
        data.append(`items[${next}][id]`, id); data.append(`items[${next}][quantity]`, '1');
        existing.add(id); next++;
      }
      picker.querySelector('[role="status"]').textContent = '';
    }
  };
})();
